import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { useAuthStore } from '@/stores/authStore'
import { useEntrepreneurAccesses } from '@/hooks/useEntrepreneurAccesses'
import { UserRole } from '@/types/auth'
import { ePath } from '@/utils/paths'
import { carrying } from '@/lib/accountActivation'

/**
 * Gate for the FoundersHub side of the entrepreneur portal -- the B2C twin of
 * `GaiaParticipantRoute`. An entrepreneur's permissions follow their platform
 * access: FoundersHub surfaces require a `foundershub` row.
 *
 * Unlike Gaia (brand new, nobody has stray access), FoundersHub has run for
 * years with no access-row concept at all, and the data migration that
 * backfills a `foundershub` row for every existing entrepreneur ships after
 * this gate does. So the empty-array case -- no access rows at all -- must
 * degrade to today's behaviour (allow) rather than 403. Once ANY row exists
 * (e.g. a gaia-only entrepreneur), the absence of a `foundershub` row is a
 * real denial.
 */
export const FoundersHubParticipantRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation()
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const { accesses, isLoading: accessesLoading } = useEntrepreneurAccesses()

  if (isLoading || accessesLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    )
  }

  if (!isAuthenticated || user?.role !== UserRole.ENTREPRENEUR) {
    window.location.href = carrying(ePath('/login'))
    return null
  }

  const hasAnyAccess = accesses && accesses.length > 0
  const foundershubAccess = accesses?.find((access) => access.platform === 'foundershub')

  if (hasAnyAccess && !foundershubAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold text-destructive">403</h1>
          <p className="text-muted-foreground">
            {t('foundershub.participant.noAccess')}
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
